<template>
  <div class="home">
    <section class="hero">
      <div class="hero__backdrop" aria-hidden="true"></div>
      <div class="container hero__inner">
        <div class="hero__copy" data-reveal>
          <p class="hero__eyebrow">GAOREN ROBOTICS</p>
          <h1 class="hero__title">工业生产装备定制 · 机器人与职业教育技术服务</h1>
          <p class="hero__subtitle">
            依据 2026 企业宣传册，本站已整合 MES/ERP/WMS/OA 工业软件、数字孪生、智慧用电与光伏云平台，以及工业机器人、AGV、人型机器人与职业教育实训装备等完整产品线。
          </p>
          <div class="hero__actions">
            <button type="button" class="btn btn--primary" @click="goProducts(defaultCategoryId)">
              查看产品中心
            </button>
            <button type="button" class="btn btn--secondary btn--hero" @click="scrollToConsult">
              获取定制方案
            </button>
          </div>
          <ul class="hero__tags">
            <li v-for="tag in heroTags" :key="tag">{{ tag }}</li>
          </ul>
          <div class="hero__stats">
            <article v-for="stat in heroStats" :key="stat.label" class="hero__stat">
              <strong>
                <CountUpNumber
                  v-if="stat.value != null"
                  :value="stat.value"
                  :suffix="stat.suffix || ''"
                />
                <template v-else>{{ stat.text }}</template>
              </strong>
              <span>{{ stat.label }}</span>
            </article>
          </div>
        </div>

        <div class="hero__visual" data-reveal data-reveal-delay="2">
          <div
            class="hero__visual-panel"
            @mouseenter="pauseShowcase"
            @mouseleave="resumeShowcase"
            @focusin="pauseShowcase"
            @focusout="resumeShowcase"
          >
            <div class="hero__showcase" aria-label="高仁产品展示">
              <div class="hero__showcase-stage">
                <button
                  type="button"
                  class="hero__showcase-nav hero__showcase-nav--prev"
                  aria-label="上一个产品"
                  @click.stop="prevShowcase"
                >
                  <span aria-hidden="true"></span>
                </button>

                <button
                  type="button"
                  class="hero__showcase-main"
                  :class="`hero__showcase-main--${activeShowcase.tone}`"
                  @click="goProducts(activeShowcase.categoryId)"
                >
                  <Transition name="showcase-fade" mode="out-in">
                    <figure :key="activeShowcase.id" class="hero__showcase-figure">
                      <div class="hero__showcase-media">
                        <img
                          :src="activeShowcase.src"
                          :alt="activeShowcase.label"
                          decoding="async"
                          fetchpriority="high"
                        />
                      </div>
                    </figure>
                  </Transition>
                </button>

                <button
                  type="button"
                  class="hero__showcase-nav hero__showcase-nav--next"
                  aria-label="下一个产品"
                  @click.stop="nextShowcase"
                >
                  <span aria-hidden="true"></span>
                </button>
              </div>

              <div class="hero__showcase-caption">
                <div class="hero__showcase-caption-text">
                  <em>{{ activeShowcase.group }}</em>
                  <strong>{{ activeShowcase.label }}</strong>
                </div>
                <span class="hero__showcase-index">
                  {{ String(showcaseIndex + 1).padStart(2, '0') }}
                  <small>/ {{ String(heroShowcase.length).padStart(2, '0') }}</small>
                </span>
              </div>

              <div class="hero__showcase-progress" aria-hidden="true">
                <i :style="{ width: `${((showcaseIndex + 1) / heroShowcase.length) * 100}%` }"></i>
              </div>

              <div
                ref="showcaseThumbsRef"
                class="hero__showcase-thumbs"
                role="tablist"
                aria-label="产品快速切换"
              >
                <button
                  v-for="(item, idx) in heroShowcase"
                  :key="`thumb-${item.id}`"
                  type="button"
                  class="hero__showcase-thumb"
                  :class="{ 'is-active': idx === showcaseIndex }"
                  :title="item.label"
                  :aria-label="item.label"
                  :aria-selected="idx === showcaseIndex"
                  role="tab"
                  @click="goShowcase(idx)"
                >
                  <img :src="item.src" :alt="item.label" loading="lazy" decoding="async" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section class="trust-bar">
        <div class="client-marquee">
          <div class="client-marquee__track-wrap">
            <div class="client-marquee__track">
              <span v-for="(client, i) in trustClientsLoop" :key="`${client}-${i}`" class="client-marquee__chip">
                {{ client }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div class="container hero__brochure">
        <div class="brochure-strip__inner cta-banner" data-reveal>
          <div class="brochure-strip__copy">
            <span class="section-eyebrow">2026 Brochure</span>
            <h2 class="section-title">企业宣传册内容已映射到本站</h2>
            <p>
              公司简介、服务范围与五大产品分类现已在网站中完整呈现，您可在线浏览各模块，或下载 PDF 存档。
            </p>
          </div>
          <div class="brochure-strip__actions">
            <a
              class="btn btn--primary"
              :href="company.brochurePdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              下载 2026 宣传册 PDF
            </a>
            <button type="button" class="btn btn--secondary btn--hero" @click="goProducts(defaultCategoryId)">
              进入产品中心
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="intent">
      <div class="container intent__grid">
        <article
          v-for="(item, idx) in intentCards"
          :key="item.title"
          class="intent__card"
          data-reveal
          :data-reveal-delay="String(idx + 1)"
        >
          <p class="intent__label">{{ item.label }}</p>
          <h2>{{ item.title }}</h2>
          <p>{{ item.desc }}</p>
          <button type="button" class="intent__link" @click="item.action">
            {{ item.cta }}
          </button>
        </article>
      </div>
    </section>

    <section id="brochure-preview" class="brochure-preview">
      <div class="container">
        <div class="section-head" data-reveal>
          <div>
            <span class="section-eyebrow">Preview</span>
            <h2 class="section-title">宣传册精选预览</h2>
          </div>
          <router-link :to="{ name: 'downloads' }" class="section-link">前往下载中心</router-link>
        </div>
        <div class="brochure-preview__grid">
          <figure
            v-for="(page, idx) in brochurePreviewPages"
            :key="page.page"
            class="brochure-preview__page"
            data-reveal
            :data-reveal-delay="String(idx % 4 + 1)"
          >
            <div class="brochure-preview__frame">
              <img :src="page.src" :alt="`宣传册第${page.page}页`" loading="lazy" />
            </div>
            <figcaption>{{ page.label }}</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section class="product-section">
      <div class="container">
        <div class="section-head" data-reveal>
          <div>
            <span class="section-eyebrow">Product Matrix</span>
            <h2 class="section-title">五大产品线 · 全场景覆盖</h2>
          </div>
          <button type="button" class="section-link" @click="goProducts(defaultCategoryId)">查看全部产品</button>
        </div>
        <div class="product-grid">
          <article
            v-for="(item, idx) in productCards"
            :key="item.title"
            class="product-card"
            data-reveal
            :data-reveal-delay="String(idx + 1)"
          >
            <div class="product-card__body">
              <p class="product-card__eyebrow">{{ item.eyebrow }}</p>
              <h3>{{ item.title }}</h3>
              <p class="product-card__desc">{{ item.desc }}</p>
              <ul class="product-card__meta">
                <li v-for="point in item.points" :key="point">{{ point }}</li>
              </ul>
              <button type="button" class="product-card__cta" @click="goProducts(item.categoryId)">
                进入分类
              </button>
            </div>
            <div class="product-card__media">
              <img :src="item.img" :alt="item.title" />
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="solution-section">
      <div class="container">
        <div class="section-head" data-reveal>
          <div>
            <span class="section-eyebrow">Solutions</span>
            <h2 class="section-title">按行业问题组织解决方案</h2>
          </div>
          <button type="button" class="section-link" @click="goSolutions('cases')">查看方案中心</button>
        </div>
        <div class="solution-grid">
          <article
            v-for="(item, idx) in solutionCards"
            :key="item.title"
            class="solution-card"
            data-reveal
            :data-reveal-delay="String(idx + 1)"
          >
            <img :src="item.img" :alt="item.title" class="solution-card__image" :style="item.imageStyle" />
            <div class="solution-card__overlay" :style="item.overlayStyle"></div>
            <div class="solution-card__body">
              <p class="solution-card__tag">{{ item.tag }}</p>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
              <ul>
                <li v-for="point in item.points" :key="point">{{ point }}</li>
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section class="capability-section">
      <div class="container capability-section__inner">
        <div class="capability-copy" data-reveal>
          <span class="section-eyebrow">Why Gaoren</span>
          <h2 class="section-title">为什么选择高仁科技</h2>
          <p>
            从工业软件到实训装备，从机器人整机到云平台运维，高仁科技以定制开发为核心，为制造企业与院校提供可落地、可扩展的完整解决方案。
          </p>
        </div>
        <div class="capability-grid">
          <article
            v-for="(item, idx) in capabilityCards"
            :key="item.title"
            class="capability-card"
            data-reveal
            :data-reveal-delay="String(idx + 1)"
          >
            <p class="capability-card__index">0{{ idx + 1 }}</p>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="case-section">
      <div class="container">
        <div class="section-head" data-reveal>
          <div>
            <span class="section-eyebrow">Case Highlights</span>
            <h2 class="section-title">典型案例与应用场景</h2>
          </div>
          <button type="button" class="section-link" @click="goSolutions('cases')">查看更多案例</button>
        </div>
        <div class="case-grid">
          <a
            v-for="(card, idx) in caseCards"
            :key="card.title"
            href="#"
            class="case-card"
            data-reveal
            :data-reveal-delay="String(idx + 1)"
            @click.prevent="goProducts(card.categoryId)"
          >
            <img :src="card.img" :alt="card.title" class="case-card__image" :style="card.imageStyle" />
            <div class="case-card__mask" :style="card.maskStyle"></div>
            <div class="case-card__body">
              <p class="case-card__industry">{{ card.industry }}</p>
              <h3>{{ card.title }}</h3>
              <p>{{ card.desc }}</p>
            </div>
          </a>
        </div>
      </div>
    </section>

    <section id="consult-section" class="consult-section">
      <div class="container consult-section__grid">
        <div class="consult-copy" data-reveal>
          <span class="section-eyebrow">Inquiry</span>
          <h2 class="section-title">告诉我们你的项目场景，我们来给出产品与方案建议</h2>
          <p class="consult-copy__desc">
            适合初步询价、定制开发沟通、院校实训建设和集成项目咨询。你也可以直接通过电话或邮箱联系我们。
          </p>
          <div class="consult-copy__contact">
            <div>
              <span>电话</span>
              <strong>{{ company.hotline }}</strong>
            </div>
            <div>
              <span>手机</span>
              <strong>{{ company.phone }}</strong>
            </div>
            <div>
              <span>邮箱</span>
              <strong>{{ company.email }}</strong>
            </div>
            <div>
              <span>地址</span>
              <strong>{{ company.address }}</strong>
            </div>
          </div>
        </div>

        <form class="consult-form" data-reveal data-reveal-delay="2" @submit.prevent="submitInquiry">
          <label>
            <span>姓名</span>
            <input v-model.trim="inquiryForm.name" type="text" placeholder="怎么称呼您" />
          </label>
          <label>
            <span>电话</span>
            <input v-model.trim="inquiryForm.phone" type="text" placeholder="便于快速联系" />
          </label>
          <label>
            <span>公司 / 学校</span>
            <input v-model.trim="inquiryForm.organization" type="text" placeholder="填写单位名称" />
          </label>
          <label>
            <span>需求类型</span>
            <select v-model="inquiryForm.needType">
              <option v-for="item in needTypes" :key="item" :value="item">{{ item }}</option>
            </select>
          </label>
          <label class="consult-form__full">
            <span>项目需求</span>
            <textarea
              v-model.trim="inquiryForm.message"
              rows="5"
              placeholder="例如：需要 AGV 搬运方案、学校实训平台建设、非标自动化产线改造等"
            ></textarea>
          </label>
          <div class="consult-form__actions">
            <button type="submit" class="btn btn--primary">提交咨询</button>
            <button type="button" class="btn btn--ghost" @click="fillDemoMessage">填入示例需求</button>
          </div>
        </form>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { defaultCategoryId } from '../data/productCatalog'
import { company, brochurePreviewPages, clientGroups } from '../data/siteConfig'
import { useScrollReveal } from '../composables/useScrollReveal'
import CountUpNumber from '../components/CountUpNumber.vue'

const router = useRouter()
const route = useRoute()
useScrollReveal()

const heroTags = ['系统类', '教育类', '机器人类', 'AGV类', '非标自动化类']
const heroShowcase = [
  {
    id: 'humanoid',
    label: '人形机器人',
    group: '机器人类',
    src: '/images/brochure/img-p4-18.jpeg',
    tone: 'product',
    categoryId: 'humanoid',
  },
  {
    id: 'quadruped',
    label: '四足机器人',
    group: '机器人类',
    src: '/images/brochure/embed-p4-15.jpeg',
    tone: 'product',
    categoryId: 'quadruped',
  },
  {
    id: 'forklift',
    label: '叉车 AGV',
    group: 'AGV类',
    src: '/images/brochure/embed-p5-4.jpeg',
    tone: 'product',
    categoryId: 'agv',
  },
  {
    id: 'mobile',
    label: '移动底盘',
    group: 'AGV类',
    src: '/images/brochure/embed-p5-1.jpeg',
    tone: 'product',
    categoryId: 'mobile-platform',
  },
  {
    id: 'composite',
    label: '复合机器人',
    group: '非标自动化',
    src: '/images/brochure/embed-p5-9.png',
    tone: 'product',
    categoryId: 'original-products',
  },
  {
    id: 'amr',
    label: 'AMR 底盘',
    group: 'AGV类',
    src: '/images/amr-chassis.png',
    tone: 'product',
    categoryId: 'mobile-platform',
  },
  {
    id: 'arm',
    label: '工业机械臂',
    group: '教育实训',
    src: '/images/brochure/img-p11-5.jpeg',
    tone: 'product',
    categoryId: 'estun-trainer',
  },
  {
    id: 'drone',
    label: '工业无人机',
    group: '机器人类',
    src: '/images/brochure/img-p6-4.png',
    tone: 'product',
    categoryId: 'drone',
  },
  {
    id: 'power',
    label: '智慧用电系统',
    group: '系统类',
    src: '/images/systems/power-curve.png',
    tone: 'product',
    categoryId: 'software',
  },
  {
    id: 'pv',
    label: '光伏云平台',
    group: '系统类',
    src: '/images/systems/pv-dashboard.png',
    tone: 'product',
    categoryId: 'energy-system',
  },
  {
    id: 'training',
    label: '智能产线实训',
    group: '教育类',
    src: '/images/brochure/img-p11-3.jpeg',
    tone: 'product',
    categoryId: 'smart-line',
  },
  {
    id: 'automation',
    label: '非标自动化产线',
    group: '非标自动化',
    src: '/images/brochure/img-p7-5.jpeg',
    tone: 'product',
    categoryId: 'automation-line',
  },
]

const showcaseIndex = ref(0)
const showcasePaused = ref(false)
const showcaseThumbsRef = ref(null)
let showcaseTimer = null

const activeShowcase = computed(() => heroShowcase[showcaseIndex.value] || heroShowcase[0])

function goShowcase(idx) {
  const len = heroShowcase.length
  showcaseIndex.value = ((idx % len) + len) % len
  nextTick(() => {
    const scroller = showcaseThumbsRef.value
    const active = scroller?.querySelector('.hero__showcase-thumb.is-active')
    if (!scroller || !active) return
    // 只横向滚动缩略图条，避免 scrollIntoView 把整页拽回顶部
    const left = active.offsetLeft - (scroller.clientWidth - active.offsetWidth) / 2
    scroller.scrollTo({ left: Math.max(0, left), behavior: 'smooth' })
  })
}

function nextShowcase() {
  goShowcase(showcaseIndex.value + 1)
}

function prevShowcase() {
  goShowcase(showcaseIndex.value - 1)
}

function pauseShowcase() {
  showcasePaused.value = true
}

function resumeShowcase() {
  showcasePaused.value = false
}

function stopShowcaseTimer() {
  if (showcaseTimer) {
    clearInterval(showcaseTimer)
    showcaseTimer = null
  }
}

function startShowcaseTimer() {
  stopShowcaseTimer()
  showcaseTimer = setInterval(() => {
    if (!showcasePaused.value) nextShowcase()
  }, 4200)
}

function onShowcaseKeydown(event) {
  const tag = event.target?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
  if (event.key === 'ArrowRight') {
    nextShowcase()
  } else if (event.key === 'ArrowLeft') {
    prevShowcase()
  }
}

const heroStats = [
  { value: 2020, label: '公司创建于天津南开' },
  { value: 5, suffix: ' 大类', label: '系统 / 教育 / 机器人 / AGV / 非标' },
  { value: null, text: '全流程', label: '方案、产品、系统与交付服务' },
]

const trustClients = clientGroups.flatMap((g) => g.clients)
const trustClientsLoop = computed(() => [...trustClients, ...trustClients])

const productCards = [
  {
    eyebrow: 'Systems',
    title: '系统类',
    desc: 'MES、ERP、WMS、OA 工业软件定制，数字孪生、节能管理与预见性点检，以及自研智慧用电与光伏云平台。',
    points: ['工业软件系统定制', '电能风能与新能源系统', '工业互联网平台'],
    categoryId: 'software',
    img: '/images/systems/pv-dashboard.png',
  },
  {
    eyebrow: 'Education',
    title: '教育类',
    desc: '智能智造产线实训、工业机器人技能装置、数字教材与资源库、技能大赛支持与校企科研合作。',
    points: ['实训平台与装置', '数字教材与资源库', '技能大赛与科研合作'],
    categoryId: 'smart-line',
    img: '/images/brochure/img-p12-3.jpeg',
  },
  {
    eyebrow: 'Robotics',
    title: '机器人类',
    desc: '人型机器人、运维机器狗、AI 定制开发平台与工业无人机，覆盖服务展示、巡检与科研应用。',
    points: ['人型机器人', '运维机器狗', 'AI 与无人机定制'],
    categoryId: 'humanoid',
    img: '/images/brochure/img-p4-18.jpeg',
  },
  {
    eyebrow: 'AGV',
    title: 'AGV类',
    desc: '潜伏顶升、叉车 AGV、复合机器人与 AMR 底盘，支持仓储物流与柔性制造搬运场景。',
    points: ['AGV / AMR 定制', '多机调度对接', '仓储与搬运场景'],
    categoryId: 'agv',
    img: '/images/brochure/img-p5-9.png',
  },
  {
    eyebrow: 'Automation',
    title: '非标自动化类',
    desc: '自动化产线非标定制、原创工业级产品、案例效果图展示与主要服务客户名录。',
    points: ['自动化产线定制', '原创工业级产品', '案例与客户展示'],
    categoryId: 'automation-line',
    img: '/images/brochure/img-p7-5.jpeg',
  },
]

const solutionCards = [
  {
    tag: '智能制造',
    title: '制造企业自动化升级',
    desc: '帮助企业处理搬运、上下料、柔性工位协同和数字化调度问题。',
    points: ['提升产线节拍与一致性', '降低人工重复作业成本', '支持非标自动化定制'],
    img: '/images/competition-training.png',
    imageStyle: {
      objectFit: 'cover',
      objectPosition: 'center center',
    },
  },
  {
    tag: '仓储物流',
    title: '物流搬运与仓内协同',
    desc: '围绕物料搬运、叉取、转运和多机调度，提供可扩展的移动机器人方案。',
    points: ['潜伏顶升与叉车形态', '支持场内复杂路径', '可对接上层业务系统'],
    img: '/images/brochure/img-p5-6.jpeg',
    imageStyle: {
      objectFit: 'contain',
      objectPosition: 'center bottom',
      padding: '28px 18px 96px',
      background: 'linear-gradient(180deg, #f9fbff 0%, #e9f0fb 100%)',
    },
    overlayStyle: {
      background: 'linear-gradient(180deg, rgba(18, 38, 75, 0.02), rgba(18, 38, 75, 0.75))',
    },
  },
  {
    tag: '职业教育',
    title: '实训基地与课程建设',
    desc: '面向院校建设实训平台、竞赛工位和综合教学环境，兼顾教学与展示。',
    points: ['实训室整体规划', '设备与课程配套', '校企合作场景支持'],
    img: '/images/brochure/img-p7-5.jpeg',
    imageStyle: {
      objectFit: 'contain',
      objectPosition: 'center center',
      padding: '22px 18px 104px',
      background: 'linear-gradient(180deg, #fcfdff 0%, #eef4fb 100%)',
    },
    overlayStyle: {
      background: 'linear-gradient(180deg, rgba(18, 38, 75, 0.04), rgba(18, 38, 75, 0.76))',
    },
  },
]

const capabilityCards = [
  {
    title: '定制开发与本地化部署',
    desc: 'MES、ERP、WMS、OA、节能管理、预见性点检与数字孪生等系统均可按项目定制，支持私有化与本地部署。',
  },
  {
    title: '职业教育全链路服务',
    desc: '从教学仪器、世赛设备改造到数字教材与资源库，再到创新大赛与校企科研合作，覆盖院校建设全流程。',
  },
  {
    title: '自研云平台可演示',
    desc: '智慧用电与智慧光伏按功能模块展示，配真实系统截图，直观呈现监测、报表、报警与运维能力。',
  },
  {
    title: '南开华创大厦 · 全国服务',
    desc: '公司位于天津市南开区金平路8号华创大厦5楼，面向制造企业与院校提供咨询、建设与培训服务。',
  },
]

const caseCards = [
  {
    industry: '制造业',
    title: '智能制造产线定制开发',
    desc: '整合机器人、工业软件与数字孪生能力，支持制造企业自动化升级和非标产线交付。',
    img: '/images/brochure/img-p8-5.jpeg',
    categoryId: 'automation-line',
    imageStyle: {
      objectFit: 'contain',
      objectPosition: 'center center',
      padding: '24px 22px 96px',
      background: 'linear-gradient(180deg, #fbfdff 0%, #eef3fa 100%)',
    },
    maskStyle: {
      background: 'linear-gradient(180deg, rgba(12, 20, 34, 0.02), rgba(12, 20, 34, 0.78))',
    },
  },
  {
    industry: '职业教育',
    title: '职业教育实训基地方案',
    desc: '覆盖实训平台、数字资源、技能竞赛支持和科研合作等综合教学场景。',
    img: '/images/competition-training.png',
    categoryId: 'smart-line',
    imageStyle: {
      objectFit: 'cover',
      objectPosition: 'center center',
    },
  },
  {
    industry: '系统平台',
    title: '智慧用电与工业软件',
    desc: '自研智慧用电云平台各功能模块，配合 MES/ERP/WMS/OA 等工业软件定制方案同站展示。',
    img: '/images/systems/power-curve.png',
    categoryId: 'software',
    imageStyle: {
      objectFit: 'cover',
      objectPosition: 'center center',
    },
    maskStyle: {
      background: 'linear-gradient(180deg, rgba(12, 20, 34, 0.12), rgba(12, 20, 34, 0.78))',
    },
  },
]

const inquiryForm = reactive({
  name: '',
  phone: '',
  organization: '',
  needType: '产品咨询',
  message: '',
})

const needTypes = [
  '工业软件 / 云平台',
  '院校实训建设',
  '视觉培训报名',
  '技能大赛支持',
  '机器人与 AGV',
  '非标自动化产线',
]

const intentCards = [
  {
    label: '01',
    title: '我想先看产品',
    desc: '快速查看机器人、软件和教学设备分类，判断是否匹配当前项目。',
    cta: '进入产品中心',
    action: () => goProducts(defaultCategoryId),
  },
  {
    label: '02',
    title: '我更关心行业方案',
    desc: '从制造、物流、教育场景出发，看高仁通常怎么组织解决方案。',
    cta: '查看解决方案',
    action: () => goSolutions('cases'),
  },
  {
    label: '03',
    title: '我需要尽快沟通需求',
    desc: '直接提交项目需求，适合询价、非标定制和实训建设咨询。',
    cta: '发起咨询',
    action: () => scrollToConsult(),
  },
]

function goProducts(categoryId) {
  router.push({ name: 'products', params: { categoryId } })
}

function goSolutions(tab = 'cases') {
  router.push({ name: 'solutions', params: { tab } })
}

function scrollToConsult() {
  nextTick(() => {
    const target = document.getElementById('consult-section')
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  })
}



function fillDemoMessage() {
  inquiryForm.message =
    '我们计划做一套面向仓储搬运的移动机器人方案，需要支持现场调度与后续扩展，请联系我沟通可行性。'
}

function submitInquiry() {
  if (!inquiryForm.name || !inquiryForm.phone || !inquiryForm.message) {
    ElMessage.warning('请先填写姓名、电话和项目需求。')
    return
  }

  ElMessage.success('咨询信息已记录，我们会尽快联系您。')
  inquiryForm.name = ''
  inquiryForm.phone = ''
  inquiryForm.organization = ''
  inquiryForm.needType = '产品咨询'
  inquiryForm.message = ''
}

function syncConsultIntent() {
  if (route.query.section === 'consult') {
    scrollToConsult()
  }
}

onMounted(() => {
  syncConsultIntent()
  startShowcaseTimer()
  window.addEventListener('keydown', onShowcaseKeydown)
})
onUnmounted(() => {
  stopShowcaseTimer()
  window.removeEventListener('keydown', onShowcaseKeydown)
})
watch(() => route.query.section, syncConsultIntent)
</script>

<style scoped>
.home {
  color: var(--text-primary);
  overflow-x: hidden;
}

.hero {
  position: relative;
  padding: 64px 0 24px;
  overflow: hidden;
}

.hero__backdrop {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 22%, rgba(28, 97, 210, 0.18), transparent 28%),
    radial-gradient(circle at 82% 18%, rgba(12, 155, 206, 0.16), transparent 24%),
    linear-gradient(135deg, #08162b 0%, #123061 42%, #18478f 100%);
}

.hero__inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1.02fr) minmax(440px, 0.98fr);
  gap: 32px;
  align-items: stretch;
}

.hero__copy {
  display: flex;
  flex-direction: column;
  color: #fff;
  min-height: 100%;
}

.hero__eyebrow {
  margin: 0 0 18px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 3px;
  opacity: 0.72;
}

.hero__title {
  margin: 0;
  max-width: 720px;
  font-size: 48px;
  line-height: 1.16;
  font-weight: 800;
}

.hero__subtitle {
  margin: 22px 0 0;
  max-width: 620px;
  font-size: 17px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.86);
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 32px;
}

.hero .btn--primary {
  border: 1px solid #4dc3ff;
  background: #4dc3ff;
  color: #08203e;
  box-shadow: 0 16px 32px rgba(77, 195, 255, 0.22);
}

.hero .btn--hero {
  border: 1px solid rgba(255, 255, 255, 0.26);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  backdrop-filter: blur(12px);
}

.hero__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  list-style: none;
  margin: 24px 0 0;
  padding: 0;
}

.hero__tags li {
  padding: 8px 14px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 13px;
  color: rgba(255, 255, 255, 0.92);
}

.hero__visual {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.hero__visual-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0;
  min-height: 100%;
  padding: 22px 22px 18px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 28px;
  background:
    radial-gradient(ellipse 78% 42% at 50% 100%, rgba(77, 195, 255, 0.1), transparent 58%),
    radial-gradient(circle at 12% 16%, rgba(28, 97, 210, 0.22), transparent 30%),
    linear-gradient(165deg, #061324 0%, #0d2a55 48%, #15407c 100%);
  box-shadow:
    0 30px 80px rgba(7, 16, 40, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.hero__showcase {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  color: #fff;
}

.hero__showcase-stage {
  position: relative;
  flex: 1;
  min-height: 320px;
  display: flex;
  align-items: center;
}

.hero__showcase-nav {
  position: absolute;
  top: 50%;
  z-index: 3;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  background: rgba(7, 18, 40, 0.7);
  backdrop-filter: blur(10px);
  color: #fff;
  cursor: pointer;
  transform: translateY(-50%);
  outline: none;
  transition:
    background 0.25s var(--ease-premium),
    border-color 0.25s ease,
    box-shadow 0.25s ease;
}

.hero__showcase-nav--prev {
  left: 6px;
}

.hero__showcase-nav--next {
  right: 6px;
}

.hero__showcase-nav span {
  position: absolute;
  inset: 0;
  margin: auto;
  width: 9px;
  height: 9px;
  border-right: 2px solid rgba(255, 255, 255, 0.92);
  border-bottom: 2px solid rgba(255, 255, 255, 0.92);
}

.hero__showcase-nav--prev span {
  transform: translateX(2px) rotate(135deg);
}

.hero__showcase-nav--next span {
  transform: translateX(-2px) rotate(-45deg);
}

.hero__showcase-nav:hover,
.hero__showcase-nav:focus-visible {
  background: rgba(77, 195, 255, 0.24);
  border-color: rgba(77, 195, 255, 0.75);
  box-shadow: 0 8px 20px rgba(77, 195, 255, 0.18);
}

.hero__showcase-main {
  position: relative;
  display: block;
  width: 100%;
  margin: 0;
  padding: 8px 48px 4px;
  min-height: 380px;
  border: 0;
  border-radius: 18px;
  background: transparent;
  cursor: pointer;
  overflow: hidden;
  outline: none;
}

.hero__showcase-figure {
  position: relative;
  z-index: 1;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 360px;
}

.hero__showcase-media {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 360px;
}

.hero__showcase-figure img {
  position: relative;
  z-index: 1;
  width: min(100%, 440px);
  height: min(100%, 340px);
  max-width: 440px;
  max-height: 340px;
  object-fit: contain;
  object-position: center center;
  image-rendering: auto;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  padding: 10px;
  box-sizing: border-box;
  filter: none;
}

.hero__showcase-caption {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  min-height: 44px;
  padding: 2px 4px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 12px;
}

.hero__showcase-caption-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.hero__showcase-caption-text em {
  font-style: normal;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(77, 195, 255, 0.95);
}

.hero__showcase-caption-text strong {
  font-size: 20px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.01em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hero__showcase-index {
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.78);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  padding-bottom: 4px;
}

.hero__showcase-index small {
  margin-left: 4px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.42);
}

.hero__showcase-progress {
  height: 2px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
}

.hero__showcase-progress i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2aa8e0, #4dc3ff);
  transition: width 0.45s var(--ease-premium);
}

.hero__showcase-thumbs {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 2px 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(77, 195, 255, 0.35) transparent;
}

.hero__showcase-thumbs::-webkit-scrollbar {
  height: 4px;
}

.hero__showcase-thumbs::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(77, 195, 255, 0.35);
}

.hero__showcase-thumb {
  position: relative;
  flex: 0 0 58px;
  width: 58px;
  height: 58px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.94);
  cursor: pointer;
  outline: none;
  box-shadow: none;
  transform: none;
  overflow: hidden;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
}

.hero__showcase-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center center;
  display: block;
  opacity: 0.78;
  transition: opacity 0.2s ease;
}

.hero__showcase-thumb.is-active {
  border-color: #4dc3ff;
  background: rgba(77, 195, 255, 0.14);
  box-shadow: 0 0 0 1px rgba(77, 195, 255, 0.25);
}

.hero__showcase-thumb.is-active img,
.hero__showcase-thumb:hover img {
  opacity: 1;
}

.hero__showcase-thumb:hover,
.hero__showcase-thumb:focus-visible {
  border-color: rgba(77, 195, 255, 0.55);
  outline: none;
}

.showcase-fade-enter-active,
.showcase-fade-leave-active {
  transition: opacity 0.28s var(--ease-premium), transform 0.28s var(--ease-premium);
}

.showcase-fade-enter-from {
  opacity: 0;
  transform: translateX(16px);
}

.showcase-fade-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}

.hero__signal {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
  padding: 8px 12px;
  border-radius: 14px;
  background: rgba(9, 20, 44, 0.55);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: right;
}

.hero__signal strong {
  font-size: 16px;
  line-height: 1.2;
}

.hero__signal span {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.3;
}

.hero__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: auto;
  padding-top: 28px;
}

.hero__stat {
  padding: 16px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(10px);
  box-shadow: none;
  text-align: left;
}

.hero__stat strong {
  display: block;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
}

.hero__stat span {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.68);
}

.hero__brochure {
  position: relative;
  z-index: 1;
  margin-top: 20px;
}

.trust-bar {
  position: relative;
  z-index: 1;
  padding: 12px 0 4px;
}

.brochure-strip__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  flex-wrap: wrap;
}

.brochure-strip__copy .section-title {
  color: #fff;
  font-size: 26px;
}

.brochure-strip__copy .section-title::after {
  display: none;
}

.brochure-strip__copy p {
  margin: 12px 0 0;
  max-width: 640px;
  font-size: 14px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.88);
}

.brochure-strip__copy .section-eyebrow {
  color: rgba(255, 255, 255, 0.75);
}

.brochure-strip__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.intent {
  margin-top: -18px;
  position: relative;
  z-index: 2;
  padding-bottom: 24px;
}

.intent__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.intent__card {
  padding: 28px;
  border-radius: var(--radius-lg);
  background: var(--surface-card);
  border: 1px solid var(--border-soft);
  box-shadow: var(--shadow-card);
  transition: transform 0.3s var(--ease-premium), box-shadow 0.3s var(--ease-premium);
}

.intent__card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-elevated);
}

.intent__label {
  margin: 0 0 14px;
  font-size: 13px;
  font-weight: 700;
  color: var(--brand-primary);
}

.intent__card h2 {
  margin: 0;
  font-size: 24px;
}

.intent__card p {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.75;
  color: var(--text-secondary);
}

.intent__link {
  margin-top: 22px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  color: var(--brand-primary);
  cursor: pointer;
}

.brochure-preview,
.product-section,
.solution-section,
.capability-section,
.case-section,
.consult-section {
  padding: var(--section-gap) 0;
}

.brochure-preview {
  background: transparent;
}

.brochure-preview .section-link {
  text-decoration: none;
}

.brochure-preview__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}

.brochure-preview__page {
  margin: 0;
}

.brochure-preview__frame {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--shadow-md);
  background: #fff;
  transition: transform 0.35s var(--ease-out-expo), box-shadow 0.35s;
}

.brochure-preview__frame:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-lg);
}

.brochure-preview__frame img {
  width: 100%;
  display: block;
  aspect-ratio: 3/4;
  object-fit: cover;
}

.brochure-preview__page figcaption {
  margin-top: 10px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.product-section,
.solution-section,
.case-section,
.consult-section {
  padding: 84px 0;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

.product-card {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-xl);
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  overflow: hidden;
  box-shadow: var(--shadow-card);
  transition: transform 0.35s var(--ease-premium), box-shadow 0.35s var(--ease-premium);
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-elevated);
}

.product-card__body {
  padding: 28px 28px 18px;
}

.product-card__eyebrow {
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #3773da;
}

.product-card h3 {
  margin: 0;
  font-size: 24px;
}

.product-card__desc {
  margin: 14px 0 0;
  min-height: 74px;
  font-size: 14px;
  line-height: 1.75;
  color: #59667d;
}

.product-card__meta {
  list-style: none;
  margin: 18px 0 0;
  padding: 0;
}

.product-card__meta li {
  position: relative;
  padding-left: 16px;
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.65;
  color: #344055;
}

.product-card__meta li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 8px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--brand-primary);
}

.product-card__cta {
  margin-top: 18px;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  color: #1c56b4;
  cursor: pointer;
}

.product-card__media {
  margin-top: auto;
  min-height: 220px;
  padding: 10px 18px 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at top, rgba(43, 89, 195, 0.12), transparent 60%);
}

.product-card__media img {
  max-width: 100%;
  max-height: 200px;
  object-fit: contain;
}

.solution-section {
  background: linear-gradient(180deg, #f4f8fe 0%, #edf3fb 100%);
}

.solution-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

.solution-card {
  position: relative;
  min-height: 420px;
  border-radius: 26px;
  overflow: hidden;
  color: #fff;
  box-shadow: 0 22px 48px rgba(16, 40, 84, 0.12);
}

.solution-card__image,
.solution-card__overlay {
  position: absolute;
  inset: 0;
}

.solution-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.solution-card__overlay {
  background: linear-gradient(180deg, rgba(7, 17, 38, 0.1), rgba(7, 17, 38, 0.86));
}

.solution-card__body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: end;
  height: 100%;
  padding: 28px;
}

.solution-card__tag {
  margin: 0 0 12px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: #9ad7ff;
}

.solution-card h3 {
  margin: 0;
  font-size: 26px;
}

.solution-card p {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.86);
}

.solution-card ul {
  margin: 18px 0 0;
  padding-left: 18px;
}

.solution-card li {
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.6;
}

.capability-section {
  padding: 88px 0;
}

.capability-section__inner {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 32px;
  align-items: start;
}

.capability-copy p {
  margin: 18px 0 0;
  font-size: 15px;
  line-height: 1.8;
  color: #5f6a7e;
}

.capability-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.capability-card {
  padding: 24px;
  border-radius: 22px;
  background: #fff;
  border: 1px solid #e5edf9;
  box-shadow: 0 12px 30px rgba(17, 40, 75, 0.05);
}

.capability-card__index {
  margin: 0 0 14px;
  font-size: 12px;
  font-weight: 700;
  color: var(--brand-primary);
}

.capability-card h3 {
  margin: 0;
  font-size: 20px;
}

.capability-card p {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.75;
  color: #5d677b;
}

.case-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}

.case-card {
  position: relative;
  min-height: 380px;
  border-radius: 24px;
  overflow: hidden;
  color: #fff;
  text-decoration: none;
}

.case-card__image,
.case-card__mask {
  position: absolute;
  inset: 0;
}

.case-card__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.case-card__mask {
  background: linear-gradient(180deg, rgba(12, 20, 34, 0.06), rgba(12, 20, 34, 0.85));
}

.case-card__body {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: end;
  height: 100%;
  padding: 26px;
}

.case-card__industry {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: #90d8ff;
}

.case-card h3 {
  margin: 0;
  font-size: 24px;
}

.case-card__body p:last-child {
  margin: 12px 0 0;
  font-size: 14px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.88);
}

.consult-section {
  background: linear-gradient(180deg, #f4f8fe 0%, #ffffff 100%);
}

.consult-section__grid {
  display: grid;
  grid-template-columns: 0.92fr 1.08fr;
  gap: 28px;
  align-items: start;
}

.consult-copy {
  padding: 12px 0;
}

.consult-copy__desc {
  margin: 18px 0 0;
  font-size: 15px;
  line-height: 1.85;
  color: #5c6880;
}

.consult-copy__contact {
  display: grid;
  gap: 14px;
  margin-top: 28px;
}

.consult-copy__contact div {
  padding: 18px 20px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e3ecfa;
}

.consult-copy__contact span {
  display: block;
  font-size: 12px;
  color: #7a869c;
}

.consult-copy__contact strong {
  display: block;
  margin-top: 8px;
  font-size: 17px;
  line-height: 1.5;
  color: #10284a;
}

.consult-form {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 18px;
  padding: 28px;
  border-radius: 26px;
  background: #fff;
  border: 1px solid #dce8fb;
  box-shadow: 0 18px 44px rgba(17, 40, 75, 0.08);
}

.consult-form label {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.consult-form span {
  font-size: 13px;
  font-weight: 700;
  color: #23395f;
}

.consult-form input,
.consult-form select,
.consult-form textarea {
  width: 100%;
  padding: 13px 14px;
  border: 1px solid #d4e1f6;
  border-radius: 14px;
  background: #f9fbfe;
  font: inherit;
  color: #162033;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.consult-form input:focus,
.consult-form select:focus,
.consult-form textarea:focus {
  outline: none;
  border-color: var(--brand-primary);
  box-shadow: 0 0 0 4px rgba(43, 89, 195, 0.12);
}

.consult-form textarea {
  resize: vertical;
  min-height: 140px;
}

.consult-form__full {
  grid-column: 1 / -1;
}

.consult-form__actions {
  grid-column: 1 / -1;
  display: flex;
  gap: 12px;
  padding-top: 4px;
}

@media (max-width: 960px) {
  .hero__inner {
    grid-template-columns: 1fr;
  }

  .hero__title {
    font-size: 34px;
  }

  .hero__visual-panel {
    min-height: 0;
  }

  .hero__showcase-stage,
  .hero__showcase-main {
    min-height: 320px;
  }

  .hero__showcase-figure,
  .hero__showcase-media {
    min-height: 300px;
    height: 300px;
  }

  .hero__showcase-figure img {
    max-height: 300px;
    max-width: 340px;
  }

  .hero__showcase-thumb {
    flex-basis: 52px;
    width: 52px;
    height: 52px;
  }

  .hero__stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-top: 24px;
    padding-top: 0;
  }

  .brochure-strip__inner {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 1240px) {
  .hero__inner,
  .capability-section__inner,
  .consult-section__grid {
    grid-template-columns: 1fr;
  }

  .intent__grid,
  .product-grid,
  .solution-grid,
  .case-grid,
  .brochure-preview__grid {
    grid-template-columns: 1fr;
  }

  .hero__visual-panel {
    min-height: 0;
  }

  .hero__stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin-top: 24px;
  }

  .capability-grid {
    grid-template-columns: 1fr 1fr;
  }

  .brochure-preview__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .container {
    max-width: calc(100% - 32px);
  }

  .hero {
    padding: 56px 0 48px;
  }

  .hero__title {
    font-size: 34px;
  }

  .hero__subtitle {
    font-size: 16px;
  }

  .hero__actions,
  .consult-form__actions {
    flex-direction: column;
  }

  .hero__showcase-stage {
    min-height: 240px;
  }

  .hero__showcase-nav {
    width: 34px;
    height: 34px;
  }

  .hero__showcase-main {
    min-height: 260px;
    padding: 8px 40px 4px;
  }

  .hero__showcase-figure,
  .hero__showcase-media {
    min-height: 240px;
    height: 240px;
  }

  .hero__showcase-figure img {
    max-height: 240px;
    max-width: 280px;
  }

  .hero__showcase-thumb {
    flex-basis: 48px;
    width: 48px;
    height: 48px;
    border-radius: 10px;
    padding: 0;
  }

  .hero__stats,
  .capability-grid,
  .consult-form {
    grid-template-columns: 1fr;
  }

  .section-head {
    flex-direction: column;
    align-items: start;
  }

  .section-title {
    font-size: 28px;
  }

  .intent__card,
  .product-card__body,
  .solution-card__body,
  .consult-form {
    padding: 22px;
  }

  .product-section,
  .solution-section,
  .case-section,
  .consult-section,
  .capability-section {
    padding: 64px 0;
  }
}
</style>
